// src/modules/ida/components/ChatInput.tsx
//
// What? Chat input component for IDA module.
// Why? To provide a clean input interface for sending messages.
// How? Uses JoyUI Textarea with send/clear buttons.

import React, { useState } from "react";
import { Textarea, IconButton, Stack, Tooltip, Box } from "@mui/joy";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";

interface ChatInputProps {
  onSend: (message: string) => void;
  onClear: () => void;
  disabled: boolean;
  placeholder?: string;
  hasMessages?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onClear,
  disabled,
  placeholder = "Ask me anything about your documents...",
  hasMessages = false,
}) => {
  const [message, setMessage] = useState<string>("");

  const sendDisabled = disabled || !message.trim();
  const clearDisabled = disabled || !hasMessages;

  const handleSend = () => {
    if (sendDisabled) return;
    onSend(message.trim());
    setMessage("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-end",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          bgcolor: "background.surface",
          "&:hover": { borderColor: "primary.300" },
          "&:focus-within": {
            borderColor: "primary.500",
            borderWidth: "2px",
            bgcolor: "background.level1",
          },
        }}
      >
        <Textarea
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          minRows={2}
          maxRows={12}
          sx={{
            border: "none",
            borderRadius: "12px",
            resize: "none",
            fontSize: "1rem",
            lineHeight: 1.5,
            p: 2,
            pr: 7,
            width: "100%",
            flex: 1,
            "&:focus": { outline: "none" },
            "&::placeholder": {
              color: "var(--joy-palette-text-tertiary)",
              opacity: 0.75,
            },
          }}
        />
        <Stack
          direction="column"
          spacing={1}
          sx={{
            position: "absolute",
            right: 8,
            bottom: 8,
            zIndex: 1,
          }}
        >
          {hasMessages && (
            <Tooltip title="Clear chat" placement="top">
              <IconButton
                variant="plain"
                color="neutral"
                onClick={onClear}
                disabled={clearDisabled}
                sx={{
                  "--IconButton-size": "36px",
                  "&:hover": {
                    backgroundColor: "danger.100",
                    color: "danger.600",
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Send message" placement="top">
            <IconButton
              variant="plain"
              color="primary"
              onClick={handleSend}
              disabled={sendDisabled}
              sx={{ "--IconButton-size": "36px" }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
};
